import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";

function isSameDate(date1, date2) {
  if (!date1 || !date2) return false;

  const firstDate = new Date(date1);
  const secondDate = new Date(date2);

  return (
    firstDate.getFullYear() === secondDate.getFullYear() &&
    firstDate.getMonth() === secondDate.getMonth() &&
    firstDate.getDate() === secondDate.getDate()
  );
}

export const exportOvertime = (attendanceData, startDate, endDate, selectedStatus) => {
  if (!attendanceData || attendanceData.length === 0) {
    alert("Tidak ada data untuk diexport.");
    return;
  }

  const formattedStart = new Date(startDate).toLocaleDateString("id-ID");
  const formattedEnd = new Date(endDate).toLocaleDateString("id-ID");

  const headerTitle = [`LAPORAN OVERTIME KARYAWAN`];
  const headerDateRange = [`Periode: ${formattedStart} s.d. ${formattedEnd}`];

  const headers = [
    "Employee ID",
    "Full Name",
    "Branch",
    "Division",
    "Date",
    "Schedule",
    "Time In",
    "Time Out",
    "Clock In",
    "Clock Out",
    "Before Shift Overtime Duration",
    "After Shift Overtime Duration",
    "Overtime Duration",
  ];

  const getScheduleInfo = (employee, overtimeDate) => {
    // 1. Cari user shift berdasarkan tanggal
    const userShift = (employee.user_shift || []).find((item) => {
      const shiftDate =
        item.date ||
        item.shift_date ||
        item.effective_date ||
        item.start_date;

      return shiftDate && isSameDate(shiftDate, overtimeDate);
    });

    if (userShift?.shift) {
      return {
        name: userShift.shift.name,
        timeIn: userShift.shift.time_in,
        timeOut: userShift.shift.time_out,
      };
    }

    // 2. Ambil dari schedule
    if (employee.schedule) {
      const dayName = new Date(overtimeDate).toLocaleDateString("en-US", {
        weekday: "long",
      });

      const shiftDay = (employee.schedule.shift_day || []).find(
        (item) => item.day_name === dayName
      );

      if (shiftDay?.shift) {
        return {
          name: employee.schedule.schedule_name,
          timeIn: shiftDay.shift.time_in,
          timeOut: shiftDay.shift.time_out,
        };
      }

      return {
        name: employee.schedule.schedule_name,
        timeIn: "-",
        timeOut: "-",
      };
    }

    return {
      name: "-",
      timeIn: "-",
      timeOut: "-",
    };
  };

  // Ambil hanya overtime Approved terbaru untuk setiap tanggal per employee.
  // Jika ada record dobel di tanggal yang sama, prioritas record terbaru:
  // updated_at -> created_at -> overtime_id.
  const getLatestOvertimePerDate = (overtime = []) => {
    const latestByDate = new Map();

    const getTimestamp = (item) => {
      const rawDate = item?.updated_at || item?.created_at;
      if (!rawDate) return 0;

      const timestamp = new Date(rawDate).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    overtime.forEach((item) => {
      if (!item?.date) return;

      // Laporan overtime hanya menampilkan pengajuan yang sudah Approved.
      // Rejected / Pending tidak boleh masuk ke Excel atau ikut proses dedup.
      if (String(item?.status || "").trim().toLowerCase() !== "approved") {
        return;
      }

      // Field date dari API berbentuk tanggal; potong bagian waktu jika ada.
      const dateKey = String(item.date).slice(0, 10);
      const current = latestByDate.get(dateKey);

      if (!current) {
        latestByDate.set(dateKey, item);
        return;
      }

      const itemTimestamp = getTimestamp(item);
      const currentTimestamp = getTimestamp(current);
      const itemId = Number(item?.overtime_id || 0);
      const currentId = Number(current?.overtime_id || 0);

      if (
        itemTimestamp > currentTimestamp ||
        (itemTimestamp === currentTimestamp && itemId > currentId)
      ) {
        latestByDate.set(dateKey, item);
      }
    });

    return Array.from(latestByDate.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
  };

  // Data rows
  const rows = attendanceData.flatMap((employee) => {
    return getLatestOvertimePerDate(employee.overtime || []).map((ot) => {
      const overtimeDate = new Date(ot.date);

      const dailyAttendance = (employee.attendance || []).filter(
        (attendance) => isSameDate(attendance.date, ot.date)
      );

      const checkInAttendance = dailyAttendance.find(
        (attendance) => attendance.status === "checkin"
      );

      const checkOutAttendance = dailyAttendance.find(
        (attendance) => attendance.status === "checkout"
      );

      const beforeShiftDuration =
        ot.before_shift_overtime_duration || "00:00:00";

      const afterShiftDuration =
        ot.after_shift_overtime_duration || "00:00:00";

      const totalDuration =
        toHours(beforeShiftDuration) +
        toHours(afterShiftDuration);

      const userShift = (employee.user_shift || []).find((item) =>
        isSameDate(
          item.date || item.effective_date || item.shift_date,
          ot.date
        )
      );

      const schedule = getScheduleInfo(employee, ot.date);

      return [
        employee.users_code || "-",
        employee.name || "-",
        employee.area?.name || "-",
        employee.division?.name || "-",
        overtimeDate.toLocaleDateString("id-ID"),
        schedule.name,
        schedule.timeIn,
        schedule.timeOut,
        checkInAttendance?.time || "-",
        checkOutAttendance?.time || "-",
        beforeShiftDuration,
        afterShiftDuration,
        totalDuration,
      ];
    });
  });


  const worksheetData = [headerTitle, headerDateRange, headers, ...rows];

  // Styles
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Merge title and date rows
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
  ];

  ws["A1"].s = {
    font: { bold: true, sz: 16 },
    alignment: { horizontal: "center" },
  };

  ws["A2"].s = {
    font: { italic: true, sz: 12 },
    alignment: { horizontal: "center" },
  };

  headers.forEach((_, idx) => {
    const colLetter = String.fromCharCode(65 + idx); // A, B, C, ...
    const cell = ws[`${colLetter}3`];
    if (cell) {
      cell.s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "D9D9D9" } },
        border: {
          top: { style: "thin", color: { auto: 1 } },
          bottom: { style: "thin", color: { auto: 1 } },
          left: { style: "thin", color: { auto: 1 } },
          right: { style: "thin", color: { auto: 1 } },
        },
        alignment: { horizontal: "center" },
      };
    }
  });

  //   const colWidths = new Array(headers.length).fill({ wch: 25 });
  const colWidths = [
    { wch: 15 }, // Employee ID
    { wch: 40 }, // Full Name
    { wch: 30 }, // Branch
    { wch: 25 }, // Division
    { wch: 15 }, // Date
    { wch: 25 }, // Schedule
    { wch: 12 }, // Clock In
    { wch: 12 }, // Clock Out
    { wch: 12 }, // Clock In
    { wch: 12 }, // Clock Out
    { wch: 30 }, // Before Shift Overtime Duration
    { wch: 30 }, // After Shift Overtime Duration
    { wch: 20 }, // Overtime Duration
  ];

  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Overtime Report");

  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  saveAs(
    new Blob([wbout], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `Employee_Overtime_Report_${formattedStart}_to_${formattedEnd}.xlsx`
  );
};

function toHours(timeStr) {
  if (!timeStr || timeStr === "00:00:00") return 0;
  const [hh, mm, ss] = timeStr.split(":").map(Number);
  return hh + mm / 60 + ss / 3600;
}